import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import { SITE_URL, getRouteKey, getLang, resolvePageSeo } from '@/lib/seo';
import {
  organizationSchema,
  websiteSchema,
  webPageSchema,
  articleSchema,
  breadcrumbSchema,
  faqSchema,
  type SchemaObject,
} from '@/lib/schema';

function buildSchemas(
  routeKey: string,
  pathname: string,
  homeTitle: string,
  homeDesc: string,
): SchemaObject[] {
  const url = `${SITE_URL}${pathname}`;
  const lang = getLang(pathname);
  const schemas: SchemaObject[] = [];

  if (!routeKey) {
    schemas.push(organizationSchema());
    schemas.push(websiteSchema());
    schemas.push(webPageSchema(url, homeTitle, homeDesc));
    return schemas;
  }

  if (routeKey.startsWith('allchinabuy-')) {
    const slug = routeKey.slice('allchinabuy-'.length);
    const article = articles[slug];
    if (article) {
      schemas.push(articleSchema(article, url));
      schemas.push(
        breadcrumbSchema([
          { name: 'Home', url: `${SITE_URL}/${lang}/` },
          { name: article.h1, url },
        ]),
      );
      if (article.faq && article.faq.length > 0) {
        schemas.push(faqSchema(article.faq));
      }
      return schemas;
    }
  }

  const pageSeo = resolvePageSeo(routeKey);
  if (pageSeo) {
    schemas.push(webPageSchema(url, pageSeo.title, pageSeo.description));
  }

  return schemas;
}

export default function JsonLd() {
  const location = useLocation();
  const { t } = useTranslation();
  const routeKey = getRouteKey(location.pathname);
  const schemas = buildSchemas(
    routeKey,
    location.pathname,
    t('meta.homeTitle'),
    t('meta.homeDesc'),
  );

  return (
    <>
      {schemas.map((s) => (
        <script
          key={s.id}
          id={s.id}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s.data) }}
        />
      ))}
    </>
  );
}